export interface MapView {
  /** `[lon, lat]` — MapLibre's center order. */
  readonly center: [number, number];
  readonly zoom: number;
}

const STORAGE_KEY = 'rt.mapView';

/**
 * Reads the last persisted map view, or null if absent/invalid. Validates
 * the shape and coordinate ranges so a corrupted value can't crash map init.
 */
export function loadMapView(): MapView | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { center, zoom } = parsed as Record<string, unknown>;
    if (!Array.isArray(center) || center.length !== 2) return null;
    const [lon, lat] = center as unknown[];
    if (
      typeof lon !== 'number' ||
      typeof lat !== 'number' ||
      typeof zoom !== 'number' ||
      !Number.isFinite(lon) ||
      !Number.isFinite(lat) ||
      !Number.isFinite(zoom) ||
      lat < -90 ||
      lat > 90 ||
      lon < -180 ||
      lon > 180
    ) {
      return null;
    }
    return { center: [lon, lat], zoom };
  } catch {
    return null;
  }
}

/** Persists the current map view; ignores storage failures (quota/private mode). */
export function saveMapView(center: [number, number], zoom: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ center, zoom }));
  } catch {
    // Storage unavailable (quota exceeded / private mode) — non-fatal.
  }
}
